// src/utils/acceptanceEmailComposer.js
// Pure-logic layer that turns a deal + its contacts + Settings defaults
// into a flat context object suitable for {{placeholder}} substitution.

import moment from "moment";
import { interpolate } from "./emailPlaceholders";
import {
  DEFAULT_ACCEPTANCE_TEMPLATES,
} from "./acceptanceEmails";

const PRONOUN_SUBJECT = { he: "He", she: "She" };

function firstNameOf(name) {
  if (!name) return "";
  const t = String(name).trim();
  return t ? t.split(/\s+/)[0] : "";
}

function joinFirstNamesAmp(contacts) {
  const firsts = (contacts || [])
    .map((c) => c?.firstName || firstNameOf(c?.name))
    .filter(Boolean);
  if (firsts.length === 0) return "";
  if (firsts.length === 1) return firsts[0];
  return firsts.slice(0, -1).join(", ") + " & " + firsts[firsts.length - 1];
}

function joinFullNames(contacts) {
  return (contacts || [])
    .map((c) => c?.name ||
      [c?.firstName, c?.lastName].filter(Boolean).join(" "))
    .filter(Boolean)
    .join(", ");
}

function fmtLongDate(yyyyMmDd) {
  if (!yyyyMmDd) return "";
  return moment(yyyyMmDd).format("MMMM D, YYYY");
}

function contactBlockLines(contacts) {
  const lines = [];
  for (const c of contacts || []) {
    const name = c?.name || [c?.firstName, c?.lastName].filter(Boolean).join(" ");
    const email = c?.email || "";
    const phone = c?.phone || "";
    const parts = [name, email, phone].filter(Boolean);
    if (parts.length > 0) lines.push(parts.join(" · "));
  }
  return lines.join("\n");
}

function currentAddressLine(contacts, propertyAddress) {
  // Surfaces a "current address" line only when a contact has an address
  // that's clearly different from the deal property address.
  for (const c of contacts || []) {
    const a = c?.address || {};
    const parts = [a.street, a.city, a.state].filter(Boolean);
    if (parts.length === 0) continue;
    const oneLine = parts.join(", ");
    if (propertyAddress && a.street &&
        String(propertyAddress).toLowerCase().includes(String(a.street).toLowerCase())) {
      continue;
    }
    return `Current address: ${oneLine}`;
  }
  return "";
}

export function buildContext(deal, contacts, settingsDefaults) {
  const d = deal || {};
  const s = settingsDefaults || {};
  const isBuyer = d.dealtype === "Buyer";

  const ae = d.acceptanceEmails || {};
  const lender = ae.lender || {};
  const inspection = ae.inspection || {};
  const otherTitle = ae.otherSideTitle || {};

  const clientNames = joinFullNames(contacts);
  const clientFirsts = joinFirstNamesAmp(contacts);

  const otherSideContact =
    [otherTitle.email, otherTitle.phone].filter(Boolean).join(" · ");

  // When no agent is assigned on the deal, the Team Lead acts as the
  // agent for client-facing communication. Mirrors the credit rule in
  // utils/dealCredit.js so the acceptance email never says "Agent: blank"
  // on a deal the team lead is personally working.
  const effectiveAgentName = d.agentName || d.teamleadName || "";

  // Financing type is free text on the deal ("Cash", "cash", "CASH", …).
  const isCash = String(d.financingType || "").trim().toLowerCase() === "cash";

  return {
    // Always present
    propertyAddress: d.propertyaddress || "",
    agentName: effectiveAgentName,
    agentFirstName: firstNameOf(effectiveAgentName),

    // Side / party labels
    sideLabel: isBuyer ? "buyers" : "listing",
    partyLabel: isBuyer ? "Buyers" : "Sellers",
    otherSideLabel: isBuyer ? "Listing" : "Buyer",

    // Client roll-up
    sellerNames: !isBuyer ? clientFirsts : "",
    buyerNames: isBuyer ? clientFirsts : "",
    clientNames,
    clientContactBlock: contactBlockLines(contacts),
    clientCurrentAddressLine: currentAddressLine(contacts, d.propertyaddress),

    // Dates
    inspectionContingencyEnd: fmtLongDate(d.inspectionDeadline),
    closingDate: fmtLongDate(d.closingdate),
    financingDeadline: fmtLongDate(d.financingDeadline),

    // A cash buyer has no lender, so there is nothing to be approved for and
    // no written statement to wait on. The whole line is composed here rather
    // than written into the template body, so on a cash deal it disappears
    // instead of leaving a dangling label with no date after it.
    writtenStatementLine: isCash
      ? ""
      : `Written Statement: The due date for Buyer to provide Seller with a Written Statement from the lender stating that Buyer is approved for the loan is ${fmtLongDate(d.financingDeadline)}`,

    inspectionDate: fmtLongDate(inspection.date),
    walkthroughTime: inspection.walkthroughTime || "",
    inspectorName: inspection.name || "",

    // Lender
    lenderName: lender.name || "",
    lenderPronounSubject: PRONOUN_SUBJECT[lender.pronoun] || "",

    // Team defaults (our title)
    ourTitleCompany: s.titleCompany || "",
    ourTitleCloserName: s.titleCloserName || "",
    ourTitleCloserFirstName: firstNameOf(s.titleCloserName),
    ourTitleCloserEmail: s.titleCloserEmail || "",
    ourTitleCloserPhone: s.titleCloserPhone || "",

    // Other-side title (per-deal)
    otherSideTitleCompany: otherTitle.company || "",
    otherSideTitleCloserName: otherTitle.closerName || "",
    otherSideTitleCloserContact: otherSideContact,

    // Commissions / fees
    brokerAdminFee:
      ae.brokerAdminFeeOverride != null
        ? Number(ae.brokerAdminFeeOverride)
        : Number(s.brokerAdminFee || 0),
    totalCommissionPct: Number(d.totalcommissionrate || 0),
    buyerSideCommissionPct: Number(d.buyersidecommissionrate || 0),
  };
}

const REQUIRED_BY_TEMPLATE = {
  "seller-client-accepted": [
    "sellerNames",
    "propertyAddress",
    "inspectionContingencyEnd",
    "agentFirstName",
    "ourTitleCloserName",
    "ourTitleCompany",
  ],
  "buyer-client-accepted": [
    "buyerNames",
    "propertyAddress",
    "inspectionContingencyEnd",
    "inspectionDate",
    "walkthroughTime",
    "inspectorName",
    "agentFirstName",
    "ourTitleCloserName",
    "ourTitleCompany",
    "lenderName",
    "lenderPronounSubject",
  ],
  "title-new-transaction": [
    "ourTitleCloserFirstName",
    "agentName",
    "propertyAddress",
    "brokerAdminFee",
    "totalCommissionPct",
    "buyerSideCommissionPct",
    "clientNames",
    "otherSideTitleCompany",
    "otherSideTitleCloserName",
  ],
};

export function missingFieldsFor(templateId, ctx) {
  const required = REQUIRED_BY_TEMPLATE[templateId] || [];
  return required.filter((k) => {
    const v = ctx?.[k];
    if (v === 0) return false;
    return v == null || v === "";
  });
}

export function composeAcceptanceEmail(templateId, deal, contacts, sender, settingsDefaults, overrides) {
  const ctx = buildContext(deal, contacts, settingsDefaults);
  const base = DEFAULT_ACCEPTANCE_TEMPLATES[templateId];
  const ov = overrides || {};
  const subject = interpolate(ov.subject ?? base.subject, ctx);
  const text = interpolate(ov.body ?? base.body, ctx);
  return { subject, text, ctx };
}
